// NocoBase API Service for Almikial Law Company Website
class NocoBaseService {
  constructor() {
    // NocoBase API URL - Force correct URL
    // IMPORTANT: Must use this exact URL: https://almikial-website-u25217.vm.elestio.app
    const envUrl = import.meta.env.VITE_NOCOBASE_API_URL;
    // Always use the correct URL (almikial-website, not almikial-pmo)
    const defaultUrl = 'https://almikial-website-u25217.vm.elestio.app';
    
    // Remove trailing slash and /api if present
    let baseUrl = envUrl || defaultUrl;
    
    // Fix incorrect URL if it contains 'almikial-pmo'
    if (baseUrl.includes('almikial-pmo')) {
      console.warn('⚠️ Detected incorrect URL (almikial-pmo), fixing to almikial-website');
      baseUrl = baseUrl.replace('almikial-pmo', 'almikial-website');
    }
    
    // Ensure we're using the correct URL
    const correctUrl = 'https://almikial-website-u25217.vm.elestio.app';
    if (!baseUrl.includes('almikial-website-u25217.vm.elestio.app')) {
      console.warn('⚠️ URL does not match expected base URL. Using correct URL.');
      baseUrl = correctUrl;
    }
    
    baseUrl = baseUrl.replace(/\/+$/, ''); // Remove trailing slashes
    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.replace(/\/api$/, '');
    }
    
    // Final check - ensure we have the correct URL
    if (baseUrl !== correctUrl) {
      console.warn(`⚠️ URL mismatch! Expected: ${correctUrl}, Got: ${baseUrl}. Using correct URL.`);
      baseUrl = correctUrl;
    }
    
    this.apiUrl = baseUrl;
    this.apiBase = `${this.apiUrl}/api`;
    
    // Log the exact URL being used
    console.log('🔗 NocoBase Base URL:', this.apiUrl);
    console.log('🔗 NocoBase API Base:', this.apiBase);
    console.log('✅ Confirmed: Using correct database at https://almikial-website-u25217.vm.elestio.app');
    
    // NocoBase API Token - SECURITY: Must be provided via environment variable
    // SECURITY FIX: Removed hardcoded default token to prevent exposure
    const envToken = import.meta.env.VITE_NOCOBASE_API_TOKEN;
    
    if (!envToken || envToken.trim() === '') {
      console.error('❌ SECURITY ERROR: VITE_NOCOBASE_API_TOKEN is required but not set');
      console.error('💡 Please set VITE_NOCOBASE_API_TOKEN in your .env.local file');
      throw new Error('NocoBase API token is required. Please set VITE_NOCOBASE_API_TOKEN environment variable.');
    }
    
    this.apiToken = envToken;
    
    // Validate token format (JWT tokens are typically long)
    if (this.apiToken.length < 50) {
      console.warn('⚠️ NocoBase API token appears to be invalid (too short)');
    } else {
      console.log('✅ Using NocoBase API token from environment variable');
    }
    
    // Cache for API responses (5 minutes TTL)
    this.dataCache = new Map();
    this.cacheTTL = 5 * 60 * 1000; // 5 minutes
    
    // Log configuration with token info
    const tokenPreview = this.apiToken ? `${this.apiToken.substring(0, 30)}...${this.apiToken.substring(this.apiToken.length - 10)}` : 'Missing';
    const tokenSource = import.meta.env.VITE_NOCOBASE_API_TOKEN ? 'Environment Variable' : 'Hardcoded Default';
    
    console.log('🔧 NocoBase Configuration:', {
      apiUrl: this.apiUrl,
      apiBase: this.apiBase,
      apiToken: tokenPreview,
      tokenSource: tokenSource,
      hasEnvToken: !!import.meta.env.VITE_NOCOBASE_API_TOKEN,
      envUrl: envUrl || 'using default'
    });
    
    // Warn if using environment variable that might be outdated
    if (import.meta.env.VITE_NOCOBASE_API_TOKEN) {
      console.log('💡 Using token from environment variable. If you see 401 errors, check if token is valid.');
    }
    
    if (!this.apiToken) {
      console.error('❌ NocoBase API token missing. Please set VITE_NOCOBASE_API_TOKEN environment variable.');
    } else {
      console.log('✅ NocoBase integration ready!');
    }
  }
  
  // Get cache key for a request
  getCacheKey(endpoint, params = {}) {
    const paramStr = JSON.stringify(params);
    return `${endpoint}:${paramStr}`;
  }
  
  // Check if cached data is still valid
  isCacheValid(cacheEntry) {
    if (!cacheEntry) return false;
    return Date.now() - cacheEntry.timestamp < this.cacheTTL;
  }
  
  // Generic API request method for NocoBase
  async request(endpoint, options = {}) {
    // Verify we're using the correct database URL
    const expectedUrl = 'https://almikial-website-u25217.vm.elestio.app';
    if (!this.apiUrl.includes('almikial-website-u25217.vm.elestio.app')) {
      console.error('❌ ERROR: Using incorrect database URL!');
      console.error(`   Current URL: ${this.apiUrl}`);
      console.error(`   Expected URL: ${expectedUrl}`);
      throw new Error(`Incorrect database URL. Must use: ${expectedUrl}`);
    }
    
    if (!this.apiToken) {
      throw new Error('NocoBase API token not configured');
    }

    // Build URL - ensure no double /api
    let url;
    if (endpoint.startsWith('http')) {
      url = endpoint;
    } else {
      // Remove leading slash from endpoint if present
      const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
      // Ensure apiBase doesn't have trailing slash
      const cleanApiBase = this.apiBase.replace(/\/+$/, '');
      url = `${cleanApiBase}/${cleanEndpoint}`;
    }
    
    const config = {
      method: options.method || 'GET',
      headers: {
        'Authorization': `Bearer ${this.apiToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      ...options,
    };
    
    // Ensure headers are not overridden
    if (options.headers) {
      Object.assign(config.headers, options.headers);
    }

    if (options.body) {
      config.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
    }
    
    // Log request in development
    if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      console.log(`🔗 NocoBase Request: ${config.method} ${url}`);
      console.log(`   Token preview: ${this.apiToken ? `${this.apiToken.substring(0, 30)}...${this.apiToken.substring(this.apiToken.length - 10)}` : 'Missing'}`);
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        // Handle 401 Unauthorized
        if (response.status === 401) {
          console.error('❌ NocoBase API: 401 Unauthorized - token might be invalid or expired');
          console.error('💡 Current token:', this.apiToken ? `${this.apiToken.substring(0, 30)}...` : 'Missing');
          console.error('💡 Token source:', import.meta.env.VITE_NOCOBASE_API_TOKEN ? 'Environment Variable' : 'Hardcoded');
          console.error('💡 Please check:');
          console.error('   1. Token is valid and not expired');
          console.error('   2. Token has root/admin permissions');
          console.error('   3. If using .env, update VITE_NOCOBASE_API_TOKEN');
          return { data: [], meta: {} };
        }
        
        // Handle 403 Forbidden
        if (response.status === 403) {
          console.warn('⚠️ NocoBase API: 403 Forbidden - insufficient permissions');
          return { data: [], meta: {} };
        }
        
        // Handle 404 for missing collections
        if (response.status === 404) {
          console.warn(`⚠️ Collection/endpoint not found: ${endpoint}`);
          return null;
        }
        
        // Handle other errors
        const errorText = await response.text();
        console.error(`❌ NocoBase API error: ${response.status} ${response.statusText}`);
        console.error(`   Error: ${errorText.substring(0, 200)}`);
        return { data: [], meta: {} };
      }
      
      return await response.json();
    } catch (error) {
      console.error('NocoBase API request failed:', error);
      return { data: [], meta: {} };
    }
  }

  // Get hero banners from NocoBase
  async getHeroBanners(activeOnly = true, language = 'en') {
    try {
      console.log('🔄 Fetching hero banners from NocoBase...');
      console.log(`   API Base: ${this.apiBase}`);
      
      const collectionName = 'Hero_Banners';
      
      // Use trusted endpoint format from documentation: /{collectionName}:list
      // This is the correct format according to NocoBase API docs
      const endpoints = [
        `${collectionName}:list`,  // ✅ Primary: /Hero_Banners:list (trusted format)
        `collections/${collectionName}:list`,  // Alternative
        `collections/${collectionName}/records`,
        `${collectionName}/records`,
      ];
      
      let response = null;
      let workingEndpoint = null;
      
      // Try each endpoint format
      for (const endpoint of endpoints) {
        try {
          // Build query parameters
          const params = new URLSearchParams();
          params.append('pageSize', '100');
          params.append('appends', 'Media'); // ✅ Include Media field in response (required for file/attachment fields)
          
          // For :list endpoints, params go in query string
          const fullEndpoint = endpoint.includes(':') 
            ? `${endpoint}?${params.toString()}`
            : `${endpoint}?${params.toString()}`;
          
          console.log(`   Trying endpoint: ${endpoint}`);
          const testResponse = await this.request(fullEndpoint);
          
          // Check if we got data
          if (testResponse && testResponse !== null) {
            let hasData = false;
            let recordCount = 0;
            
            // Check different response structures
            if (testResponse.data) {
              if (Array.isArray(testResponse.data) && testResponse.data.length > 0) {
                hasData = true;
                recordCount = testResponse.data.length;
              } else if (testResponse.data.data && Array.isArray(testResponse.data.data) && testResponse.data.data.length > 0) {
                hasData = true;
                recordCount = testResponse.data.data.length;
              } else if (testResponse.data.rows && Array.isArray(testResponse.data.rows) && testResponse.data.rows.length > 0) {
                hasData = true;
                recordCount = testResponse.data.rows.length;
              }
            } else if (Array.isArray(testResponse) && testResponse.length > 0) {
              hasData = true;
              recordCount = testResponse.length;
            }
            
            // Accept if we have data or if response structure is valid (even if empty)
            if (hasData || (testResponse.data !== undefined && !testResponse.errors)) {
              response = testResponse;
              workingEndpoint = endpoint;
              console.log(`   ✅ Working endpoint: ${endpoint}${recordCount > 0 ? ` (${recordCount} records)` : ''}`);
              break;
            }
          }
        } catch (error) {
          // Continue to next endpoint
          if (!error.message.includes('403') && !error.message.includes('Forbidden')) {
            console.log(`   ⚠️  ${endpoint}: ${error.message}`);
          }
          continue;
        }
      }
      
      if (!response) {
        console.warn('⚠️ Could not access Hero_Banners records');
        console.warn('💡 Possible issues:');
        console.warn('   1. Collection is not linked to database table');
        console.warn('   2. Collection needs to be connected to "Hero" table in PostgreSQL');
        console.warn('   3. Check NocoBase Admin → Collections → Hero_Banners → Settings');
        console.warn('   4. Enable "Connect to database view" and select "Hero" table');
        return [];
      }
      
      // Extract records from response
      let records = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          records = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          records = response.data.data;
        } else if (response.data.rows && Array.isArray(response.data.rows)) {
          records = response.data.rows;
        }
      } else if (Array.isArray(response)) {
        records = response;
      }
      
      console.log(`✅ Fetched ${records.length} hero banner(s) from NocoBase`);
      
      // Format records
      const formattedBanners = this.formatHeroBanners(records, language);
      
      // Filter active banners if needed
      if (activeOnly) {
        const filtered = formattedBanners.filter(banner => {
          const active = banner.active === true || banner.active === 'true' || banner.active === 1;
          const activeStatus = banner.Active_Status || banner.ActiveStatus;
          const isApproved = activeStatus === 'Approved' || activeStatus === 'Approve';
          
          return active && isApproved;
        });
        
        console.log(`✅ Filtered to ${filtered.length} active banner(s)`);
        return filtered;
      }
      
      return formattedBanners;
    } catch (error) {
      console.warn('Failed to fetch hero banners from NocoBase:', error);
      return [];
    }
  }

  // Format hero banners data
  formatHeroBanners(records, language = 'en') {
    if (!records || !Array.isArray(records)) {
      return [];
    }

    return records.map(record => {
      const fields = record;
      
      // Get image from Media field (NocoBase file uploads return arrays)
      // Priority: Media (confirmed field name in Hero_Banners) > Image > background_image > media
      const imageField = fields.Media || fields['Media'] || fields.Image || fields.image || fields.background_image || fields.backgroundImage || fields.media;
      const imageUrl = imageField ? this.getFileUrl(imageField) : null;
      
      // Debug logging for hero images
      if (imageField && !imageUrl) {
        console.warn('⚠️ Hero banner image field found but could not parse URL:', {
          bannerId: fields.id || fields.Id,
          imageField: imageField,
          imageFieldType: typeof imageField
        });
      } else if (imageUrl) {
        console.log('✅ Hero banner image found:', {
          bannerId: fields.id || fields.Id,
          imageUrl: imageUrl
        });
      } else {
        console.log('ℹ️ No image field found for hero banner:', {
          bannerId: fields.id || fields.Id,
          availableFields: Object.keys(fields).filter(k => k.toLowerCase().includes('image') || k.toLowerCase().includes('media'))
        });
      }
      
      // Handle button text - try all possible field name variations
      // Actual NocoBase field names: "Call_to_Action_TextEng" and "Call_to_Action_TextArb"
      const buttonTextEng = 
        fields['Call_to_Action_TextEng'] ||  // ✅ Actual field name in NocoBase
        fields['Call_to_Action_Text_eng'] || 
        fields['Call to Action Text_eng'] || 
        fields['Call to Action TextEng'] || 
        fields['CallToActionTextEng'] ||
        fields['Call to Action Text Eng'] ||
        fields['call_to_action_text_eng'] ||
        fields.callToActionTextEng ||
        fields.CallToActionTextEng ||
        '';
      
      const buttonTextArb = 
        fields['Call_to_Action_TextArb'] ||  // ✅ Actual field name in NocoBase
        fields['Call_to_Action_Text_arb'] || 
        fields['Call to Action Text_arb'] || 
        fields['Call to Action TextArb'] || 
        fields['CallToActionTextArb'] ||
        fields['Call to Action Text Arb'] ||
        fields['call_to_action_text_arb'] ||
        fields.callToActionTextArb ||
        fields.CallToActionTextArb ||
        '';
      
      // Log for debugging if button text is missing
      if (!buttonTextEng && !buttonTextArb) {
        console.warn('⚠️ No button text found for banner:', {
          id: record.id || record.Id,
          availableFields: Object.keys(fields).filter(k => k.toLowerCase().includes('action') || k.toLowerCase().includes('button'))
        });
      }
      
      // Handle status fields
      let active = fields.active ?? fields.Active ?? false;
      if (typeof active === 'string') {
        active = active.toLowerCase() === 'true' || active === '1';
      } else if (typeof active === 'number') {
        active = active === 1;
      }
      
      const activeStatus = fields.Active_Status || fields.ActiveStatus || 'Pending';
      const approveStatus = fields.Approve_Status || fields.approveStatus || activeStatus;
      
      return {
        id: record.id || record.Id,
        name: fields.Name || fields.name || 'Hero Banner',
        title: language === 'ar' ? (fields.Headline_arb || fields.HeadlineArb) : (fields.Headline_eng || fields.HeadlineEng),
        title_en: fields.Headline_eng || fields.HeadlineEng,
        title_ar: fields.Headline_arb || fields.HeadlineArb,
        subtitle: language === 'ar' ? (fields.Subheadline_arb || fields.SubheadlineArb) : (fields.Subheadline_eng || fields.SubheadlineEng),
        subtitle_en: fields.Subheadline_eng || fields.SubheadlineEng,
        subtitle_ar: fields.Subheadline_arb || fields.SubheadlineArb,
        buttonText: language === 'ar' ? buttonTextArb : buttonTextEng,
        buttonText_en: buttonTextEng,
        buttonText_ar: buttonTextArb,
        buttonAction: fields.button_action || 'Open Chat',
        // Handle CustumButtonAction field (note: typo in DB field name)
        // Try all possible field name variations
        custom_button_action: 
          fields.CustumButtonAction !== undefined ? fields.CustumButtonAction :
          fields.custum_button_action !== undefined ? fields.custum_button_action :
          fields.custom_button_action !== undefined ? fields.custom_button_action :
          fields['CustumButtonAction'] !== undefined ? fields['CustumButtonAction'] :
          fields['custum_button_action'] !== undefined ? fields['custum_button_action'] :
          fields['custom_button_action'] !== undefined ? fields['custom_button_action'] :
          false,
        // Handle ButtonUrl field - try all possible variations
        button_url: 
          fields.ButtonUrl || 
          fields.button_url || 
          fields['ButtonUrl'] || 
          fields['button_url'] ||
          null,
        backgroundImage: imageUrl,
        backgroundVideo: this.getFileUrl(fields.background_video || fields.Video),
        order: fields.order || fields.Order || fields['Display Order'] || 0,
        Active_Status: activeStatus,
        activeStatus: active === true || activeStatus === 'Approved',
        active_status: active === true || activeStatus === 'Approved',
        Approve_Status: approveStatus,
        approveStatus: approveStatus,
        approve_status: approveStatus,
        active: active,
        status: fields.Status || fields.status || 'Draft'
      };
    });
  }

  // Helper: Get file URL from NocoBase file field
  getFileUrl(fileField) {
    if (!fileField) return null;
    
    let url = null;
    
    // Handle string (direct URL)
    if (typeof fileField === 'string') {
      url = fileField;
    }
    // Handle array (NocoBase file uploads often return arrays)
    else if (Array.isArray(fileField)) {
      if (fileField.length === 0) return null;
      const file = fileField[0];
      // If array contains strings (URLs)
      if (typeof file === 'string') {
        url = file;
      }
      // If array contains objects (file objects) - NocoBase format
      else if (file && typeof file === 'object') {
        // NocoBase file objects have: url, preview, filename, etc.
        // Priority: url > preview > path > downloadURL > src > thumbUrl
        url = file.url || file.preview || file.path || file.downloadURL || file.src || file.thumbUrl || null;
        // If still no URL, try to construct from id and filename
        if (!url && file.id && file.filename) {
          // NocoBase file URL format: /storage/uploads/{filename}
          url = `/storage/uploads/${file.filename}`;
        } else if (!url && file.id) {
          // Fallback: try with id
          url = `/storage/uploads/${file.id}`;
        }
      }
    }
    // Handle object (NocoBase file uploads often return objects)
    else if (typeof fileField === 'object' && fileField !== null) {
      url = fileField.url || fileField.downloadURL || fileField.path || fileField.src || fileField.thumbUrl || null;
      // If still no URL, try to construct from id
      if (!url && fileField.id) {
        // NocoBase file URL format: /storage/uploads/{id}
        url = `/storage/uploads/${fileField.id}`;
      }
    }
    
    if (!url) return null;
    
    // If URL is already absolute, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // If URL starts with /, it's a relative path from root
    if (url.startsWith('/')) {
      // For NocoBase storage paths like /storage/uploads/10-43cn75.png
      // Build full URL: https://almikial-website-u25217.vm.elestio.app/storage/uploads/...
      if (url.startsWith('/storage/')) {
        // Remove /api from base URL and add storage path
        const baseUrl = this.apiUrl.replace('/api', '');
        return `${baseUrl}${url}`;
      }
      return url;
    }
    
    // Otherwise, prepend API URL
    return `${this.apiUrl}/${url}`;
  }

  // Get services from NocoBase
  async getServices(liveOnly = true, language = 'en') {
    try {
      console.log('🔄 Fetching services from NocoBase...');
      const collectionName = 'Services';
      const endpoint = `${collectionName}:list`;
      
      const params = new URLSearchParams();
      params.append('pageSize', '100');
      params.append('appends', 'image'); // ✅ Include image field in response (required for file fields)
      
      const response = await this.request(`${endpoint}?${params.toString()}`);
      
      if (!response || !response.data) {
        console.warn('⚠️ No services data from NocoBase');
        return [];
      }
      
      let records = Array.isArray(response.data) ? response.data : [];
      
      // Debug: Log COMPLETE services data
      console.log('═══════════════════════════════════════════════════════════');
      console.log('📊 COMPLETE SERVICES DATA ANALYSIS FROM NOCOBASE');
      console.log('═══════════════════════════════════════════════════════════');
      console.log(`Total Records: ${records.length}`);
      console.log('');
      
      records.forEach((record, index) => {
        const fields = record;
        console.log(`\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
        console.log(`📋 SERVICE #${index + 1} - COMPLETE FIELD ANALYSIS`);
        console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
        
        // Get all keys
        const allKeys = Object.keys(fields);
        console.log(`\n🔑 ALL FIELDS (${allKeys.length} total):`);
        allKeys.forEach(key => {
          const value = fields[key];
          const valueType = typeof value;
          const valuePreview = valueType === 'object' && value !== null 
            ? JSON.stringify(value).substring(0, 100) 
            : String(value).substring(0, 100);
          console.log(`   • ${key}: ${valueType} = ${valuePreview}`);
        });
        
        // Basic Info
        console.log(`\n📝 BASIC INFORMATION:`);
        console.log(`   • ID: ${fields.id || fields.Id || fields.ID || 'N/A'}`);
        console.log(`   • Title (EN): ${fields.title_en || fields.Title_eng || fields.titleEn || 'N/A'}`);
        console.log(`   • Title (AR): ${fields.title_ar || fields.Title_arb || fields.titleAr || 'N/A'}`);
        console.log(`   • Name: ${fields.name || fields.Name || 'N/A'}`);
      });
      
      console.log(`\n═══════════════════════════════════════════════════════════`);
      console.log('📊 END OF SERVICES DATA ANALYSIS');
      console.log('═══════════════════════════════════════════════════════════\n');
      
      // Format services
      const formatted = records.map(record => {
        const fields = record;
        
        // Try all possible field name variations for titles
        const titleEn = 
          fields.title_en || 
          fields.Title_eng || 
          fields.titleEn || 
          fields['title_en'] ||
          fields['Title_eng'] ||
          '';
        const titleAr = 
          fields.title_ar || 
          fields.Title_arb || 
          fields.titleAr || 
          fields['title_ar'] ||
          fields['Title_arb'] ||
          '';
        
        // Try all possible field name variations for descriptions
        const descEn = 
          fields.description_en || 
          fields.Description_Eng ||
          fields.Description_eng || 
          fields.descEn || 
          fields['description_en'] ||
          fields['Description_Eng'] ||
          fields['Description_eng'] ||
          '';
        const descAr = 
          fields.description_ar || 
          fields.Description_Arb ||
          fields.Description_arb || 
          fields.descAr || 
          fields['description_ar'] ||
          fields['Description_Arb'] ||
          fields['Description_arb'] ||
          '';
        
        // Try all possible field name variations for status
        const status = 
          fields.Status || 
          fields.status || 
          fields['Status'] ||
          fields['status'] ||
          'Draft';
        
        // Determine if active - check status and active field
        // Status can be: 'Live', 'Active', 'Approved', 'Draft', null, etc.
        // Also check active boolean field
        let isActive = false;
        if (liveOnly) {
          // Check status first (handle null/undefined)
          // Support: 'Live', 'Active', 'Approved' as active statuses
          if (status) {
            const statusLower = String(status).toLowerCase();
            if (statusLower === 'live' || statusLower === 'active' || statusLower === 'approved') {
              isActive = true;
            }
          }
          // Also check active boolean field (try all variations)
          const activeField = 
            fields.active !== undefined ? fields.active :
            fields.Active !== undefined ? fields.Active :
            fields['active'] !== undefined ? fields['active'] :
            fields['Active'] !== undefined ? fields['Active'] :
            null;
          
          if (activeField === true || activeField === 1 || activeField === '1' || String(activeField).toLowerCase() === 'true') {
            isActive = true;
          }
        } else {
          // If not filtering by liveOnly, show all
          isActive = true;
        }
        
        return {
          id: fields.id || fields.Id || fields.ID,
          name: titleEn || titleAr || 'Service',
          title: language === 'ar' ? (titleAr || titleEn) : (titleEn || titleAr),
          title_en: titleEn,
          title_ar: titleAr,
          slug: (titleEn || titleAr).toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
          description: language === 'ar' ? (descAr || descEn) : (descEn || descAr),
          description_en: descEn,
          description_ar: descAr,
          // Get image from 'image' field for Services (user confirmed using 'image' field)
          // Priority: 'image' first, then fallback to other variations
          image: (() => {
            const imageField = 
              fields.image ||        // ✅ PRIMARY: User confirmed using 'image' field
              fields['image'] ||
              fields.Image || 
              fields['Image'] ||
              fields.Media || 
              fields['Media'] ||
              fields.media ||
              fields['media'] ||
              fields.attachment ||
              fields.Attachment ||
              fields['attachment'] ||
              fields['Attachment'] ||
              fields.attachments ||
              fields.Attachments ||
              fields['attachments'] ||
              fields['Attachments'] ||
              null;
            
            const imageUrl = this.getFileUrl(imageField);
            
            // Debug logging for images
            if (imageField) {
              if (imageUrl) {
                console.log('✅ Service image found:', {
                  serviceId: fields.id || fields.Id,
                  serviceTitle: titleEn || titleAr,
                  imageUrl: imageUrl,
                  imageFieldType: typeof imageField
                });
              } else {
                console.warn('⚠️ Service image field found but could not parse URL:', {
                  serviceId: fields.id || fields.Id,
                  serviceTitle: titleEn || titleAr,
                  imageField: imageField,
                  imageFieldType: typeof imageField,
                  imageFieldString: typeof imageField === 'object' ? JSON.stringify(imageField).substring(0, 200) : String(imageField)
                });
              }
            } else {
              // Log when image field is missing (only in development)
              if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
                console.log('ℹ️ Service has no image field:', {
                  serviceId: fields.id || fields.Id,
                  serviceTitle: titleEn || titleAr,
                  availableFields: Object.keys(fields).filter(k => !k.startsWith('created') && !k.startsWith('updated')).join(', ')
                });
              }
            }
            
            return imageUrl;
          })(),
          order: fields.order || fields.Order || 0,
          active: isActive,
          status: status,
          featured: fields.featured || false
        };
      });
      
      // Filter by active if needed
      const filtered = liveOnly ? formatted.filter(s => s.active) : formatted;
      
      // Sort by order
      filtered.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      console.log(`✅ Fetched ${filtered.length} service(s) from NocoBase`);
      return filtered;
    } catch (error) {
      console.error('❌ Failed to fetch services from NocoBase:', error);
      return [];
    }
  }

  // Get subservices from NocoBase
  async getSubservices(serviceId = null, liveOnly = true, language = 'en') {
    try {
      console.log('🔄 Fetching subservices from NocoBase...');
      const collectionName = 'Subservices';
      
      // FIRST: Get ALL Collections to understand the database structure
      console.log('\n╔══════════════════════════════════════════════════════════════════════════════╗');
      console.log('║           FETCHING ALL COLLECTIONS FROM NOCOBASE                              ║');
      console.log('╚══════════════════════════════════════════════════════════════════════════════╝\n');
      
      try {
        const collectionsEndpoints = [
          'collections:list',
          'collections',
          'collections:get'
        ];
        
        let allCollections = null;
        for (const endpoint of collectionsEndpoints) {
          try {
            console.log(`   🔍 Trying collections endpoint: ${endpoint}`);
            const collectionsResponse = await this.request(endpoint);
            
            if (collectionsResponse) {
              if (collectionsResponse.data && Array.isArray(collectionsResponse.data)) {
                allCollections = collectionsResponse.data;
              } else if (Array.isArray(collectionsResponse)) {
                allCollections = collectionsResponse;
              } else if (collectionsResponse.data?.data && Array.isArray(collectionsResponse.data.data)) {
                allCollections = collectionsResponse.data.data;
              }
              
              if (allCollections && allCollections.length > 0) {
                console.log(`   ✅ SUCCESS! Found ${allCollections.length} collection(s)\n`);
                break;
              }
            }
          } catch (err) {
            console.log(`   ⚠️  ${endpoint} failed: ${err.message}`);
            continue;
          }
        }
        
        if (allCollections && allCollections.length > 0) {
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('📋 ALL COLLECTIONS IN NOCOBASE:');
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
          
          // Find Services and Subservices collections
          const servicesCollection = allCollections.find(c => 
            (c.name || c.key || '').toLowerCase() === 'services'
          );
          const subservicesCollection = allCollections.find(c => 
            (c.name || c.key || '').toLowerCase() === 'subservices'
          );
          
          allCollections.forEach((collection, index) => {
            const name = collection.name || collection.key || collection.title || 'N/A';
            const isServices = name.toLowerCase() === 'services';
            const isSubservices = name.toLowerCase() === 'subservices';
            const marker = isServices ? '🎯 SERVICES' : (isSubservices ? '🎯 SUBSERVICES' : '');
            
            console.log(`${(index + 1).toString().padStart(3, ' ')}. ${name.padEnd(30, ' ')} ${marker}`);
            console.log(`      Title: ${collection.title || 'N/A'}`);
            console.log(`      Type: ${collection.type || 'N/A'}`);
            if (collection.tableName) console.log(`      Table Name: ${collection.tableName}`);
            console.log('');
          });
          
          if (servicesCollection) {
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
            console.log('📋 SERVICES COLLECTION DETAILS:');
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
            console.log(JSON.stringify(servicesCollection, null, 2));
            console.log('');
          }
          
          if (subservicesCollection) {
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
            console.log('📋 SUBSERVICES COLLECTION DETAILS:');
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
            console.log(JSON.stringify(subservicesCollection, null, 2));
            console.log('');
          }
        } else {
          console.log('   ⚠️  Could not fetch collections list');
        }
      } catch (collectionsError) {
        console.log(`   ⚠️  Error fetching collections: ${collectionsError.message}`);
      }
      
      // SECOND: Get the ACTUAL fields schema from NocoBase
      console.log('\n╔══════════════════════════════════════════════════════════════════════════════╗');
      console.log('║           FETCHING ACTUAL FIELDS SCHEMA FROM NOCOBASE API                      ║');
      console.log('╚══════════════════════════════════════════════════════════════════════════════╝\n');
      
      try {
        // Try multiple endpoints to get fields
        const fieldsEndpoints = [
          `collections/${collectionName}/fields:list`,
          `collections:get?filter[name]=${collectionName}`,
          `${collectionName}/fields:list`,
          `collections/${collectionName}/fields`,
          `fields:list?filter[collectionName]=${collectionName}`,
          `collections/${collectionName}`
        ];
        
        let fieldsData = null;
        let collectionData = null;
        
        for (const fieldsEndpoint of fieldsEndpoints) {
          try {
            console.log(`   🔍 Trying endpoint: ${fieldsEndpoint}`);
            const fieldsResponse = await this.request(fieldsEndpoint);
            
            // Check if response contains fields
            if (fieldsResponse) {
              // Try to extract fields from different response structures
              if (fieldsResponse.data) {
                if (Array.isArray(fieldsResponse.data)) {
                  fieldsData = fieldsResponse.data;
                } else if (fieldsResponse.data.fields && Array.isArray(fieldsResponse.data.fields)) {
                  fieldsData = fieldsResponse.data.fields;
                  collectionData = fieldsResponse.data;
                } else if (fieldsResponse.data.data && Array.isArray(fieldsResponse.data.data)) {
                  fieldsData = fieldsResponse.data.data;
                }
              } else if (Array.isArray(fieldsResponse)) {
                fieldsData = fieldsResponse;
              } else if (fieldsResponse.fields && Array.isArray(fieldsResponse.fields)) {
                fieldsData = fieldsResponse.fields;
                collectionData = fieldsResponse;
              }
              
              if (fieldsData && fieldsData.length > 0) {
                console.log(`   ✅ SUCCESS! Found ${fieldsData.length} field(s) via ${fieldsEndpoint}\n`);
                break;
              }
            }
          } catch (err) {
            console.log(`   ⚠️  ${fieldsEndpoint} failed: ${err.message}`);
            continue;
          }
        }
        
        if (fieldsData && fieldsData.length > 0) {
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('📋 ACTUAL FIELDS SCHEMA FROM NOCOBASE API:');
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
          
          fieldsData.forEach((field, index) => {
            const fieldName = field.name || field.key || field.fieldName || 'N/A';
            const fieldType = field.type || field.uiSchemaType || field.dataType || 'N/A';
            const fieldTitle = field.title || field.label || 'N/A';
            
            console.log(`${(index + 1).toString().padStart(3, ' ')}. ${fieldName}`);
            console.log(`      Type: ${fieldType}`);
            console.log(`      Title: ${fieldTitle}`);
            if (field.target) console.log(`      Target Collection: ${field.target}`);
            if (field.collectionName) console.log(`      Collection: ${field.collectionName}`);
            if (field.interface) console.log(`      Interface: ${field.interface}`);
            if (field.required !== undefined) console.log(`      Required: ${field.required}`);
            if (field.unique !== undefined) console.log(`      Unique: ${field.unique}`);
            console.log(`      Full Field Object:`, JSON.stringify(field, null, 2));
            console.log('');
          });
          
          // Find linking fields
          const linkingFields = fieldsData.filter(f => {
            const name = (f.name || f.key || '').toLowerCase();
            const type = (f.type || f.uiSchemaType || '').toLowerCase();
            return type === 'linkto' || 
                   type === 'relation' ||
                   type === 'belongsto' ||
                   type === 'hasmany' ||
                   f.target === 'Services' ||
                   f.target === 'services' ||
                   name.includes('service') || 
                   name.includes('parent') ||
                   name.includes('main');
          });
          
          if (linkingFields.length > 0) {
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
            console.log('🔗 LINKING FIELDS FOUND IN SCHEMA (Fields that link to Services):');
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
            linkingFields.forEach(field => {
              const fieldName = field.name || field.key;
              console.log(`   ✅ ${fieldName}`);
              console.log(`      Type: ${field.type || field.uiSchemaType}`);
              console.log(`      Target: ${field.target || 'N/A'}`);
              console.log(`      Interface: ${field.interface || 'N/A'}`);
              console.log(`      Full Field:`, JSON.stringify(field, null, 2));
              console.log('');
            });
          } else {
            console.log('   ❌ NO LINKING FIELDS FOUND IN SCHEMA!');
            console.log('   This means the Subservices collection is NOT linked to Services collection.');
          }
        } else {
          console.log('   ⚠️  Could not fetch fields schema from NocoBase API');
          console.log('   Will analyze fields from actual records instead...');
        }
      } catch (fieldsError) {
        console.log(`   ⚠️  Error fetching fields schema: ${fieldsError.message}`);
        console.log('   Will analyze fields from actual records instead...');
      }
      
      console.log('\n╔══════════════════════════════════════════════════════════════════════════════╗');
      console.log('║           FETCHING ACTUAL RECORDS FROM NOCOBASE                               ║');
      console.log('╚══════════════════════════════════════════════════════════════════════════════╝\n');
      
      const endpoint = `${collectionName}:list`;
      const params = new URLSearchParams();
      params.append('pageSize', '100');
      params.append('appends', 'media'); // ✅ Include media field in response (required for file fields)
      
      const response = await this.request(`${endpoint}?${params.toString()}`);
      
      if (!response || !response.data) {
        console.warn('⚠️ No subservices data from NocoBase');
        return [];
      }
      
      let records = Array.isArray(response.data) ? response.data : [];
      
      // ============================================================
      // COMPREHENSIVE SUBSERVICES FIELDS ANALYSIS
      // ============================================================
      console.log('\n\n');
      console.log('╔══════════════════════════════════════════════════════════════════════════════╗');
      console.log('║                    SUBSERVICES TABLE - COMPLETE FIELDS ANALYSIS                ║');
      console.log('╚══════════════════════════════════════════════════════════════════════════════╝');
      console.log(`\n📊 Total Subservices Records: ${records.length}\n`);
      
      if (records.length === 0) {
        console.log('⚠️  NO SUBSERVICES FOUND IN DATABASE!');
        console.log('╚══════════════════════════════════════════════════════════════════════════════╝\n');
      } else {
        // Analyze first record in detail
        const firstRecord = records[0];
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('📋 SAMPLE RECORD #1 - ALL FIELDS:');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
        
        const allKeys = Object.keys(firstRecord);
        console.log(`Total Fields Found: ${allKeys.length}\n`);
        
        allKeys.forEach((key, index) => {
          const value = firstRecord[key];
          const valueType = typeof value;
          let valueStr = '';
          
          if (value === null) {
            valueStr = 'null';
          } else if (value === undefined) {
            valueStr = 'undefined';
          } else if (valueType === 'object') {
            if (Array.isArray(value)) {
              valueStr = `Array[${value.length}] = ${JSON.stringify(value).substring(0, 200)}`;
            } else {
              valueStr = `Object: ${JSON.stringify(value, null, 2).substring(0, 500)}`;
            }
          } else {
            valueStr = String(value).substring(0, 200);
          }
          
          console.log(`${(index + 1).toString().padStart(3, ' ')}. ${key.padEnd(30, ' ')} | Type: ${valueType.padEnd(10, ' ')} | Value: ${valueStr}`);
        });
        
        console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('🔗 LINKING FIELDS ANALYSIS (Fields that connect to Services table):');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
        
        const linkingFieldNames = [
          'Subservices', 'subservices',
          'Main_Service_ID', 'main_service_id', 'Main_Service_ID',
          'service_id', 'Service_ID', 'service_ID',
          'parent_service', 'Parent_Service', 'parentService',
          'Service_ID', 'service_ID',
          'mainService', 'MainService',
          'parentServiceId', 'ParentServiceId'
        ];
        
        let foundLinkingFields = [];
        linkingFieldNames.forEach(fieldName => {
          if (firstRecord.hasOwnProperty(fieldName)) {
            foundLinkingFields.push(fieldName);
            const value = firstRecord[fieldName];
            console.log(`✅ FOUND: ${fieldName}`);
            console.log(`   Type: ${typeof value}`);
            if (typeof value === 'object' && value !== null) {
              console.log(`   Object Structure:`, JSON.stringify(value, null, 2));
              if (value.id !== undefined) console.log(`   → value.id = ${value.id}`);
              if (value.Id !== undefined) console.log(`   → value.Id = ${value.Id}`);
              if (value.ID !== undefined) console.log(`   → value.ID = ${value.ID}`);
              if (Array.isArray(value) && value.length > 0) {
                console.log(`   → Array[0] = ${JSON.stringify(value[0])}`);
              }
            } else {
              console.log(`   Value: ${value}`);
            }
            console.log('');
          }
        });
        
        if (foundLinkingFields.length === 0) {
          console.log('❌ NO LINKING FIELDS FOUND!');
          console.log('   This means subservices are NOT linked to main services in the database.');
        } else {
          console.log(`\n✅ Found ${foundLinkingFields.length} linking field(s): ${foundLinkingFields.join(', ')}`);
        }
        
        console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('📋 ALL SUBSERVICES SUMMARY:');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
        
        records.forEach((record, index) => {
          const fields = record;
          const title = fields.title_en || fields.Title_eng || fields.title || 'N/A';
          const id = fields.id || fields.Id || fields.ID || 'N/A';
          
          // Extract linking value
          let linkingValue = null;
          let linkingField = null;
          
          if (fields.Subservices !== undefined) {
            linkingValue = fields.Subservices;
            linkingField = 'Subservices';
          } else if (fields.Main_Service_ID !== undefined) {
            linkingValue = fields.Main_Service_ID;
            linkingField = 'Main_Service_ID';
          } else if (fields.main_service_id !== undefined) {
            linkingValue = fields.main_service_id;
            linkingField = 'main_service_id';
          } else if (fields.service_id !== undefined) {
            linkingValue = fields.service_id;
            linkingField = 'service_id';
          } else if (fields.parent_service !== undefined) {
            linkingValue = fields.parent_service;
            linkingField = 'parent_service';
          }
          
          // Extract ID from linking value if it's an object
          let extractedServiceId = null;
          if (linkingValue !== null && linkingValue !== undefined) {
            if (typeof linkingValue === 'object') {
              extractedServiceId = linkingValue.id || linkingValue.Id || linkingValue.ID || linkingValue[0];
            } else {
              extractedServiceId = linkingValue;
            }
          }
          
          console.log(`${(index + 1).toString().padStart(3, ' ')}. ${title.padEnd(50, ' ')} | ID: ${String(id).padEnd(10, ' ')} | Linked via: ${(linkingField || 'NONE').padEnd(20, ' ')} | Service ID: ${extractedServiceId !== null ? String(extractedServiceId) : 'NULL'}`);
        });
        
        console.log('\n╚══════════════════════════════════════════════════════════════════════════════╝\n\n');
      }
      
      // Debug: Log COMPLETE raw data structure for ALL records
      console.log('═══════════════════════════════════════════════════════════');
      console.log('📊 COMPLETE SUBSERVICES DATA ANALYSIS FROM NOCOBASE');
      console.log('═══════════════════════════════════════════════════════════');
      console.log(`Total Records: ${records.length}`);
      console.log('');
      
      // Analyze ALL records
      records.forEach((record, index) => {
        const fields = record;
        console.log(`\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
        console.log(`📋 SUBSERVICE #${index + 1} - COMPLETE FIELD ANALYSIS`);
        console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
        
        // Get all keys
        const allKeys = Object.keys(fields);
        console.log(`\n🔑 ALL FIELDS (${allKeys.length} total):`);
        allKeys.forEach(key => {
          const value = fields[key];
          const valueType = typeof value;
          const valuePreview = valueType === 'object' && value !== null 
            ? JSON.stringify(value).substring(0, 100) 
            : String(value).substring(0, 100);
          console.log(`   • ${key}: ${valueType} = ${valuePreview}`);
        });
        
        // Basic Info
        console.log(`\n📝 BASIC INFORMATION:`);
        console.log(`   • ID: ${fields.id || fields.Id || fields.ID || 'N/A'}`);
        console.log(`   • Title (EN): ${fields.title_en || fields.Title_eng || fields.titleEn || 'N/A'}`);
        console.log(`   • Title (AR): ${fields.title_ar || fields.Title_arb || fields.titleAr || 'N/A'}`);
        console.log(`   • Name: ${fields.name || fields.Name || 'N/A'}`);
        
        // LINKING FIELDS - Detailed Analysis
        console.log(`\n🔗 LINKING FIELDS TO MAIN SERVICE (Detailed Analysis):`);
        
        const linkingFields = [
          'Subservices',
          'Main_Service_ID',
          'main_service_id',
          'service_id',
          'parent_service',
          'Parent_Service',
          'parentService',
          'ParentService',
          'Service_ID',
          'service_ID',
          'mainService',
          'MainService'
        ];
        
        linkingFields.forEach(fieldName => {
          if (fields[fieldName] !== undefined) {
            const value = fields[fieldName];
            const valueType = typeof value;
            console.log(`\n   ✅ FOUND: ${fieldName}`);
            console.log(`      Type: ${valueType}`);
            
            if (valueType === 'object' && value !== null) {
              console.log(`      Object Keys: ${Object.keys(value).join(', ')}`);
              console.log(`      Full Object:`, JSON.stringify(value, null, 2));
              
              // Try to extract ID from object
              const extractedId = value.id || value.Id || value.ID || value.value || value[0];
              if (extractedId !== undefined) {
                console.log(`      → Extracted ID: ${extractedId} (type: ${typeof extractedId})`);
              }
            } else {
              console.log(`      Value: ${value}`);
            }
          }
        });
        
        // Check if no linking field found
        const hasLinkingField = linkingFields.some(field => fields[field] !== undefined);
        if (!hasLinkingField) {
          console.log(`\n   ❌ NO LINKING FIELD FOUND!`);
          console.log(`   This subservice is NOT linked to any main service.`);
        }
        
        // Status and Active
        console.log(`\n📊 STATUS & ACTIVE:`);
        console.log(`   • Status: ${fields.Status || fields.status || 'N/A'}`);
        console.log(`   • Active: ${fields.active || fields.Active || 'N/A'}`);
        console.log(`   • Active Type: ${typeof (fields.active || fields.Active)}`);
      });
      
      console.log(`\n═══════════════════════════════════════════════════════════`);
      console.log('📊 END OF SUBSERVICES DATA ANALYSIS');
      console.log('═══════════════════════════════════════════════════════════\n');
      
      // Format subservices
      const formatted = records.map(record => {
        const fields = record;
        const titleEn = fields.title_en || fields.Title_eng || fields.titleEn || '';
        const titleAr = fields.title_ar || fields.Title_arb || fields.titleAr || '';
        const descEn = fields.description_en || fields.Description_eng || fields.descEn || '';
        const descAr = fields.description_ar || fields.Description_arb || fields.descAr || '';
        // Get content fields from Subservices table (richText fields)
        const contentEn = fields.content_en || fields.Content_eng || fields.contentEn || '';
        const contentAr = fields.content_ar || fields.Content_arb || fields.contentAr || '';
        // Get process steps from Subservices table (richText fields)
        const processStepsEng = fields.process_steps_eng || fields.Process_Steps_eng || fields.processStepsEng || '';
        const processStepsArb = fields.process_steps_arb || fields.Process_Steps_arb || fields.processStepsArb || '';
        // Get documents required from Subservices table (richText fields)
        const documentsRequiredEng = fields.documents_required_eng || fields.Documents_Required_eng || fields.documentsRequiredEng || '';
        const documentsRequiredArb = fields.documents_required_arb || fields.Documents_Required_arb || fields.documentsRequiredArb || '';
        // Get duration from Subservices table
        const duration = fields.duration || fields.Duration || '';
        const status = fields.Status || fields.status || 'Draft';
        
        // Get service ID from Subservices field
        // IMPORTANT: According to schema, "Subservices" field is bigInt (Foreign Key)
        // It contains the Service ID directly as a number (NOT as a relation object)
        let mainServiceId = null;
        
        // Priority 1: Subservices field (bigInt - contains Service ID directly)
        // This is the CORRECT field name according to the schema
        if (fields.Subservices !== undefined && fields.Subservices !== null) {
          // It's a number (bigInt), use it directly
          mainServiceId = Number(fields.Subservices);
          if (isNaN(mainServiceId) || mainServiceId === 0) {
            mainServiceId = null;
          }
        }
        
        // Debug logging
        if (mainServiceId) {
          console.log(`✅ Subservice "${titleEn || titleAr}" (ID: ${fields.id || fields.Id}) → Linked to Service ID: ${mainServiceId} via field: ${fields.Subservices !== undefined ? 'Subservices' : 'other'}`);
        } else {
          console.log(`⚠️  Subservice "${titleEn || titleAr}" (ID: ${fields.id || fields.Id}) → NO SERVICE LINK (Subservices field: ${fields.Subservices})`);
        }
        
        // Determine if active - check status and active field
        // Support: 'Live', 'Active', 'Approved' as active statuses
        let isActive = false;
        if (liveOnly) {
          // Check status first (handle null/undefined)
          if (status) {
            const statusLower = String(status).toLowerCase();
            if (statusLower === 'live' || statusLower === 'active' || statusLower === 'approved') {
              isActive = true;
            }
          }
          // Also check active boolean field
          const activeField = 
            fields.active !== undefined ? fields.active :
            fields.Active !== undefined ? fields.Active :
            fields['active'] !== undefined ? fields['active'] :
            fields['Active'] !== undefined ? fields['Active'] :
            null;
          
          if (activeField === true || activeField === 1 || activeField === '1' || String(activeField).toLowerCase() === 'true') {
            isActive = true;
          }
        } else {
          // If not filtering by liveOnly, show all
          isActive = true;
        }
        
        return {
          id: fields.id || fields.Id || fields.ID,
          name: titleEn || titleAr || 'Subservice',
          title: language === 'ar' ? (titleAr || titleEn) : (titleEn || titleAr),
          title_en: titleEn,
          title_ar: titleAr,
          slug: (titleEn || titleAr).toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
          description: language === 'ar' ? (descAr || descEn) : (descEn || descAr),
          description_en: descEn,
          description_ar: descAr,
          // Content fields from Subservices table (richText)
          content: language === 'ar' ? (contentAr || contentEn) : (contentEn || contentAr),
          content_en: contentEn,
          content_ar: contentAr,
          // Process steps from Subservices table (richText)
          process_steps_eng: processStepsEng,
          process_steps_arb: processStepsArb,
          // Documents required from Subservices table (richText)
          documents_required_eng: documentsRequiredEng,
          documents_required_arb: documentsRequiredArb,
          // Duration from Subservices table
          duration: duration,
          // Store linking field - Subservices is the CORRECT field name (bigInt Foreign Key)
          // Also store as normalized fields for backward compatibility
          Subservices: fields.Subservices !== undefined ? Number(fields.Subservices) : null, // Original bigInt value
          parent_service_id: mainServiceId, // Normalized for compatibility
          main_service_id: mainServiceId,   // Normalized for compatibility
          service_id: mainServiceId,         // Normalized for compatibility
          parent_service: mainServiceId,     // Normalized for compatibility
          // Get image from 'media' field for Subservices
          // According to user requirement: Subservices table has 'media' field
          // Try all possible field name variations, prioritize 'media' then 'Media'
          image: (() => {
            const mediaField = 
              fields.media || 
              fields['media'] ||
              fields.Media || 
              fields['Media'] ||
              fields.image || 
              fields.Image || 
              fields['image'] ||
              fields['Image'] ||
              null;
            
            const imageUrl = this.getFileUrl(mediaField);
            
            // Debug logging for subservice images
            if (mediaField && !imageUrl) {
              console.warn('⚠️ Subservice media field found but could not parse URL:', {
                subserviceId: fields.id || fields.Id,
                subserviceTitle: titleEn || titleAr,
                mediaField: mediaField,
                mediaFieldType: typeof mediaField
              });
            } else if (imageUrl) {
              console.log('✅ Subservice image found:', {
                subserviceId: fields.id || fields.Id,
                subserviceTitle: titleEn || titleAr,
                imageUrl: imageUrl
              });
            }
            
            return imageUrl;
          })(),
          order: fields.order || fields.Order || 0,
          active: isActive,
          status: status
        };
      });
      
      // Filter by service ID if provided
      // IMPORTANT: Subservices is the CORRECT field name (bigInt Foreign Key)
      let filtered = serviceId ? formatted.filter(s => {
        // Priority 1: Subservices field (the CORRECT field according to schema)
        let sId = null;
        if (s.Subservices !== undefined && s.Subservices !== null) {
          sId = Number(s.Subservices);
        }
        // Fallback to normalized fields for backward compatibility
        else {
          sId = s.service_id || s.main_service_id || s.parent_service_id || s.parent_service;
        }
        
        const matches = sId !== null && sId !== undefined && !isNaN(sId) && sId !== 0 && (
          String(sId) === String(serviceId) || 
          Number(sId) === Number(serviceId) ||
          parseInt(sId) === parseInt(serviceId)
        );
        
        if (!matches && sId != null) {
          console.log(`   ❌ Subservice "${s.title}" (ID: ${s.id}) has parent: ${sId} (type: ${typeof sId}), expected: ${serviceId} (type: ${typeof serviceId})`);
        }
        
        return matches;
      }) : formatted;
      
      // Log filtering result
      if (serviceId) {
        console.log(`🔍 Filtering subservices for service ID: ${serviceId}`, {
          totalSubservices: formatted.length,
          filteredCount: filtered.length,
          filteredSubservices: filtered.map(s => ({
            id: s.id,
            title: s.title,
            service_id: s.service_id,
            main_service_id: s.main_service_id,
            parent_service_id: s.parent_service_id,
            parent_service: s.parent_service
          })),
          allSubservicesWithParents: formatted.map(s => ({
            id: s.id,
            title: s.title,
            service_id: s.service_id,
            main_service_id: s.main_service_id,
            parent_service_id: s.parent_service_id,
            parent_service: s.parent_service
          }))
        });
      }
      
      // Filter by active if needed
      filtered = liveOnly ? filtered.filter(s => s.active) : filtered;
      
      // Sort by order
      filtered.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      console.log(`✅ Fetched ${filtered.length} subservice(s) from NocoBase`);
      return filtered;
    } catch (error) {
      console.error('❌ Failed to fetch subservices from NocoBase:', error);
      return [];
    }
  }

  // Get team members from NocoBase
  async getTeamMembers(activeOnly = true, language = 'en') {
    try {
      console.log('🔄 Fetching team members from NocoBase...');
      const collectionName = 'Team_Members';
      const endpoint = `${collectionName}:list`;
      
      const params = new URLSearchParams();
      params.append('pageSize', '100');
      params.append('appends', 'pic'); // ✅ Include pic field in response (required for file/attachment fields)
      
      const response = await this.request(`${endpoint}?${params.toString()}`);
      
      if (!response || !response.data) {
        console.warn('⚠️ No team members data from NocoBase');
        return [];
      }
      
      let records = Array.isArray(response.data) ? response.data : [];
      
      // Format team members
      // Note: NocoBase uses Name_eng, Name_arb, position_eng, position_arb, about_eng, about_arb
      const formatted = records.map(record => {
        const fields = record;
        
        // Name fields (NocoBase uses Name_eng, Name_arb)
        const nameEn = fields.Name_eng || fields.name_en || fields.nameEn || '';
        const nameAr = fields.Name_arb || fields.name_ar || fields.nameAr || '';
        
        // Position fields (NocoBase uses position_eng, position_arb)
        const positionEn = fields.position_eng || fields['position eng'] || fields.title_en || fields.Title_eng || '';
        const positionAr = fields.position_arb || fields['position arb'] || fields.title_ar || fields.Title_arb || '';
        
        // About/Bio fields (NocoBase uses about_eng, about_arb)
        const aboutEn = fields.about_eng || fields['about eng'] || fields.bio_en || fields.Bio_eng || '';
        const aboutAr = fields.about_arb || fields['about arb'] || fields.bio_ar || fields.Bio_arb || '';
        
        // Specialization fields
        const specializationEn = fields.specialization_eng || fields['specialization eng'] || '';
        const specializationAr = fields.specialization_arb || fields['specialization arb'] || '';
        
        // Education fields (NocoBase uses education_Eng, education_arb)
        const educationEn = fields.education_Eng || fields['education Eng'] || fields.education_eng || '';
        const educationAr = fields.education_arb || fields['education arb'] || '';
        
        // Experience fields
        const experienceEn = fields.experience_eng || fields['experience eng'] || '';
        const experienceAr = fields.experience_arb || fields['experience arb'] || '';
        
        // Languages fields
        const languagesEn = fields.languages_eng || fields['languages eng'] || '';
        const languagesAr = fields.languages_arb || fields['languages arb'] || '';
        
        // Status (NocoBase uses Status field)
        const status = fields.Status || fields.status || 'Active';
        const isActive = status === 'Active' || status === 'active' || status === true;
        
        // Helper to check if text contains HTML
        const isHtml = (text) => {
          if (!text || typeof text !== 'string') return false;
          return /<[a-z][\s\S]*>/i.test(text);
        };
        
        // Parse HTML content to plain text if needed (for lists)
        const stripHtml = (html) => {
          if (!html) return '';
          if (typeof html !== 'string') return String(html);
          return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
        };
        
        // Parse comma-separated or HTML lists (for fields that should be arrays)
        const parseList = (text) => {
          if (!text) return [];
          const plainText = stripHtml(text);
          if (!plainText) return [];
          // Split by comma, semicolon, or newline
          const items = plainText.split(/[,;\n]/).map(item => item.trim()).filter(item => item.length > 0);
          return items.length > 0 ? items : [plainText];
        };
        
        // For specialization, keep as HTML if it's rich text, otherwise parse as list
        const specializationList = parseList(specializationEn || specializationAr);
        const specializationHtml = language === 'ar' ? (specializationAr || specializationEn) : (specializationEn || specializationAr);
        
        return {
          id: fields.id || fields.Id || fields.ID,
          name: language === 'ar' ? (nameAr || nameEn) : (nameEn || nameAr),
          name_en: nameEn,
          name_ar: nameAr,
          position: language === 'ar' ? (positionAr || positionEn) : (positionEn || positionAr),
          position_en: positionEn,
          position_ar: positionAr,
          // Bio: Keep as HTML (rich text)
          bio: language === 'ar' ? (aboutAr || aboutEn) : (aboutEn || aboutAr),
          bio_en: aboutEn,
          bio_ar: aboutAr,
          bio_html: language === 'ar' ? (aboutAr || aboutEn) : (aboutEn || aboutAr),
          // Specialization: Keep as HTML if it contains HTML, otherwise use parsed list
          specialization: specializationHtml,
          specialization_list: specializationList,
          expertise: specializationList, // Alias for compatibility with MembersSection
          specialization_en: specializationEn,
          specialization_arb: specializationAr,
          specialization_html: specializationHtml,
          // Education: Keep as HTML (rich text)
          education: language === 'ar' ? (educationAr || educationEn) : (educationEn || educationAr),
          education_en: educationEn,
          education_ar: educationAr,
          education_html: language === 'ar' ? (educationAr || educationEn) : (educationEn || educationAr),
          education_list: parseList(educationEn || educationAr), // Also provide as list for backward compatibility
          // Experience: Keep as HTML (rich text)
          experience: language === 'ar' ? (experienceAr || experienceEn) : (experienceEn || experienceAr),
          experience_en: experienceEn,
          experience_ar: experienceAr,
          experience_html: language === 'ar' ? (experienceAr || experienceEn) : (experienceEn || experienceAr),
          experience_list: parseList(experienceEn || experienceAr), // Also provide as list for backward compatibility
          // Languages: Parse as list (usually simple text)
          languages: parseList(languagesEn || languagesAr),
          languages_en: languagesEn,
          languages_ar: languagesAr,
          // Get image from pic field (NocoBase uses pic field for team member images)
          image: (() => {
            // Priority: pic (NocoBase field) > image > Image > photo > Photo
            const picField = fields.pic || fields.Pic;
            if (picField) {
              const imageUrl = this.getFileUrl(picField);
              if (imageUrl) {
                console.log('✅ Team member image found:', {
                  memberId: fields.id,
                  memberName: nameEn || nameAr,
                  imageUrl: imageUrl,
                  picFieldType: typeof picField,
                  picFieldIsArray: Array.isArray(picField)
                });
              } else {
                console.warn('⚠️ Team member pic field found but could not parse URL:', {
                  memberId: fields.id,
                  memberName: nameEn || nameAr,
                  picField: picField,
                  picFieldType: typeof picField
                });
              }
              return imageUrl;
            }
            const fallbackUrl = this.getFileUrl(fields.image || fields.Image || fields.photo || fields.Photo);
            if (!fallbackUrl) {
              console.log('ℹ️ No image field found for team member:', {
                memberId: fields.id,
                memberName: nameEn || nameAr
              });
            }
            return fallbackUrl;
          })(),
          email: fields.email || fields.Email || '',
          phone: fields.phone || fields.Phone || '',
          linkedin: fields.linkedin || fields.LinkedIn || '',
          order: parseInt(fields.order || fields.Order || '0', 10),
          active: isActive,
          status: status
        };
      });
      
      // Filter by active if needed
      // Check both 'active' boolean and 'Status' field
      const filtered = activeOnly 
        ? formatted.filter(m => {
            const isActive = m.active === true || m.status === 'Active' || m.status === 'active';
            return isActive;
          })
        : formatted;
      
      // Sort by order
      filtered.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      console.log(`✅ Fetched ${filtered.length} team member(s) from NocoBase`);
      return filtered;
    } catch (error) {
      console.error('❌ Failed to fetch team members from NocoBase:', error);
      return [];
    }
  }

  // Get posts/insights from NocoBase
  async getPosts(options = {}) {
    try {
      console.log('🔄 Fetching posts from NocoBase...');
      const collectionName = 'Posts';
      const endpoint = `${collectionName}:list`;
      
      const params = new URLSearchParams();
      params.append('pageSize', '100');
      // Include image field in response (NocoBase uses 'image' field for Posts)
      // Note: Only include appends for actual associations/relations in NocoBase
      // Fields like tags, Services, Category are regular fields (not associations)
      params.append('appends', 'image');
      
      const response = await this.request(`${endpoint}?${params.toString()}`);
      
      if (!response || !response.data) {
        console.warn('⚠️ No posts data from NocoBase');
        return [];
      }
      
      let records = Array.isArray(response.data) ? response.data : [];
      
      // Format posts
      const formatted = this.formatPosts(records, options.language || 'en');
      
      // Filter by status if needed
      // Note: NocoBase uses "Approved" but we map it to "Published" for compatibility
      if (options.status) {
        const filtered = formatted.filter(post => {
          // Handle both "Published" and "Approved" status
          if (options.status === 'Published') {
            return post.status === 'Published' || post.status === 'Approved';
          }
          return post.status === options.status;
        });
        console.log(`✅ Filtered to ${filtered.length} post(s) with status: ${options.status}`);
        return filtered;
      }
      
      // Filter by Post_Type if needed
      if (options.postType) {
        const filtered = formatted.filter(post => post.Post_Type === options.postType);
        console.log(`✅ Filtered to ${filtered.length} post(s) with type: ${options.postType}`);
        return filtered;
      }
      
      console.log(`✅ Fetched ${formatted.length} post(s) from NocoBase`);
      return formatted;
    } catch (error) {
      console.error('❌ Failed to fetch posts from NocoBase:', error);
      return [];
    }
  }

  // Format posts data from NocoBase
  formatPosts(records, language = 'en') {
    if (!records || !Array.isArray(records)) {
      return [];
    }

    return records.map(record => {
      const fields = record;
      
      // Get title based on language
      const titleEn = fields.title_eng || fields.title_en || '';
      const titleAr = fields.title_ar || '';
      const title = language === 'ar' ? (titleAr || titleEn) : (titleEn || titleAr);
      
      // Get content based on language
      const contentEn = fields.content_en || '';
      const contentAr = fields.content_ar || '';
      const content = language === 'ar' ? (contentAr || contentEn) : (contentEn || contentAr);
      
      // Generate slug from title
      const slug = titleEn 
        ? titleEn.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
        : `post-${fields.id || ''}`;
      
      // Generate excerpt from content
      const stripHtml = (html) => {
        if (!html) return '';
        if (typeof html !== 'string') return String(html);
        return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').trim();
      };
      
      const excerptEn = stripHtml(contentEn).substring(0, 150) + (contentEn && contentEn.length > 150 ? '...' : '');
      const excerptAr = stripHtml(contentAr).substring(0, 150) + (contentAr && contentAr.length > 150 ? '...' : '');
      const excerpt = language === 'ar' ? (excerptAr || excerptEn) : (excerptEn || excerptAr);
      
      // Map Post_Type to Category (same mapping as NocoDB)
      const categoryMap = {
        'Articles': 'Publication',
        'Articles ': 'Publication',
        'Events': 'Legal Update',
        'Social Media': 'Insights',
        'Social Media ': 'Insights'
      };
      const category = categoryMap[fields.Post_Type] || 'Publication';
      
      // Get date
      const postDate = fields.Published_Date || fields.Date || new Date().toISOString();
      const dateStr = postDate ? new Date(postDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      
      // Get status (NocoBase uses "Approved" instead of "Published")
      // Map "Approved" to "Published" for compatibility
      const rawStatus = fields.status || 'Draft';
      const status = rawStatus === 'Approved' || rawStatus === 'Published' ? 'Published' : rawStatus;
      
      return {
        id: fields.id || fields.Id || fields.ID,
        Name: titleEn || titleAr || `Post ${fields.id || ''}`,
        title: title,
        title_eng: titleEn,
        title_ar: titleAr,
        title_en: titleEn,
        slug: slug,
        Category: category,
        category: category.toLowerCase(),
        Post_Type: fields.Post_Type || 'Articles',
        Date: dateStr,
        'Published Date': dateStr,
        date: dateStr,
        published_at: dateStr,
        Summary: excerptEn,
        excerpt: excerpt,
        excerpt_en: excerptEn,
        excerpt_ar: excerptAr,
        content: content,
        content_en: contentEn,
        content_ar: contentAr,
        status: status,
        // Get image from various possible field names
        image: (() => {
          // Try all possible image field names
          const imageField = 
            fields.image || 
            fields.Image || 
            fields.media || 
            fields.Media || 
            fields.photo || 
            fields.Photo || 
            fields.featured_image || 
            fields.Featured_image || 
            fields.background_image || 
            fields.Background_image;
          
          if (imageField) {
            const imageUrl = this.getFileUrl(imageField);
            if (imageUrl) {
              console.log('✅ Post image found:', {
                postId: fields.id,
                postTitle: titleEn || titleAr,
                imageUrl: imageUrl,
                imageFieldType: typeof imageField
              });
            } else {
              console.warn('⚠️ Post image field found but could not parse URL:', {
                postId: fields.id,
                postTitle: titleEn || titleAr,
                imageField: imageField,
                imageFieldType: typeof imageField
              });
            }
            return imageUrl;
          }
          console.log('ℹ️ No image field found for post:', {
            postId: fields.id,
            postTitle: titleEn || titleAr,
            availableFields: Object.keys(fields).filter(k => 
              k.toLowerCase().includes('image') || 
              k.toLowerCase().includes('media') || 
              k.toLowerCase().includes('photo')
            )
          });
          return null;
        })(),
        featured: fields.featured || false,
        show_on_homepage: fields.show_on_homepage || false,
        homepage_featured: fields.homepage_featured || false,
        // Get tags - could be array, string, or JSON
        tags: (() => {
          const tagsField = fields.tags || fields.Tags || fields.tag || [];
          if (Array.isArray(tagsField)) return tagsField;
          if (typeof tagsField === 'string') {
            try {
              const parsed = JSON.parse(tagsField);
              return Array.isArray(parsed) ? parsed : [tagsField];
            } catch {
              return tagsField ? [tagsField] : [];
            }
          }
          return [];
        })(),
        categoryNames: fields.categoryNames || fields.Category || [],
        teamMembers: fields.teamMembers || fields.team_members || [],
        // Get author ID - check multiple possible field names
        authorId: fields.authorId || fields.author_id || fields.Author || fields['Team Members Auther'] || 
                  (Array.isArray(fields.authorIds || fields.author_ids) && (fields.authorIds || fields.author_ids)[0]) || null,
        authorIds: fields.authorIds || fields.author_ids || 
                   (fields.authorId ? [fields.authorId] : []) || [],
        services: fields.services || fields.Services || [],
        // Get linked service IDs - check multiple possible field names
        linkedServiceIds: (() => {
          const servicesField = fields.Services || fields.services || fields['Lined_services'] || fields.linked_services || [];
          if (Array.isArray(servicesField)) {
            // If array of objects, extract IDs
            return servicesField.map(s => s?.id || s?.Id || s).filter(Boolean);
          }
          if (typeof servicesField === 'string') {
            try {
              const parsed = JSON.parse(servicesField);
              return Array.isArray(parsed) ? parsed.map(s => s?.id || s?.Id || s).filter(Boolean) : [];
            } catch {
              return [];
            }
          }
          return [];
        })()
      };
    }).filter(post => {
      // Only return published/approved posts by default
      // Map "Approved" from NocoBase to "Published" for filtering
      return post.status === 'Published';
    });
  }

  // Get service details from NocoBase
  async getServiceDetails(subserviceId, language = 'en') {
    try {
      console.log(`🔄 Fetching service details for subservice ${subserviceId} from NocoBase...`);
      const collectionName = 'Service_Details';
      const endpoint = `${collectionName}:list`;
      
      const params = new URLSearchParams();
      params.append('pageSize', '100');
      
      const response = await this.request(`${endpoint}?${params.toString()}`);
      
      if (!response || !response.data) {
        console.warn('⚠️ No service details data from NocoBase');
        return null;
      }
      
      let records = Array.isArray(response.data) ? response.data : [];
      console.log(`📊 Found ${records.length} service details records in NocoBase`);
      
      // Log first record structure for debugging
      if (records.length > 0) {
        const firstRecord = records[0];
        console.log('📋 Sample record structure:', {
          hasSubservice_ID: !!firstRecord.Subservice_ID,
          hasService_ID: !!firstRecord.Service_ID,
          hasMain_Service_ID: !!firstRecord.Main_Service_ID,
          keys: Object.keys(firstRecord).slice(0, 10)
        });
      }
      
      // Find details - check both Subservice_ID and Service_ID
      // This allows the same table to work for both main services and subservices
      const details = records.find(r => {
        const subId = r.Subservice_ID || r.subservice_id || r.SubserviceID;
        const serviceId = r.Service_ID || r.service_id || r.ServiceID || r.Main_Service_ID || r.main_service_id;
        
        // Match by Subservice_ID first (for subservices)
        if (subId && String(subId) === String(subserviceId)) {
          console.log(`✅ Matched by Subservice_ID: ${subId}`);
          return true;
        }
        
        // Match by Service_ID (for main services)
        if (serviceId && String(serviceId) === String(subserviceId)) {
          console.log(`✅ Matched by Service_ID: ${serviceId}`);
          return true;
        }
        
        return false;
      });
      
      if (!details) {
        console.warn(`⚠️ No service details found for ID ${subserviceId}`);
        console.log('🔍 Available IDs in records:', records.slice(0, 5).map(r => ({
          Subservice_ID: r.Subservice_ID,
          Service_ID: r.Service_ID,
          Main_Service_ID: r.Main_Service_ID
        })));
        return null;
      }
      
      const fields = details;
      const contentEn = fields.content_en || fields.Content_eng || fields.contentEn || '';
      const contentAr = fields.content_ar || fields.Content_arb || fields.contentAr || '';
      
      // Parse features
      let features = [];
      const featuresField = fields.features || fields.Features;
      if (featuresField) {
        try {
          if (typeof featuresField === 'string') {
            features = JSON.parse(featuresField);
          } else if (Array.isArray(featuresField)) {
            features = featuresField;
          }
        } catch (e) {
          console.warn('Failed to parse features:', e);
          features = [];
        }
      }
      
      // Parse process steps with language support
      let processSteps = [];
      const processStepsField = language === 'ar'
        ? (fields.process_steps_arb || fields.process_steps_ar || fields.process_steps || fields.Process_Steps_arb || fields.Process_Steps_ar || fields.Process_Steps)
        : (fields.process_steps_eng || fields.process_steps_en || fields.process_steps || fields.Process_Steps_eng || fields.Process_Steps_en || fields.Process_Steps);
      
      if (processStepsField) {
        try {
          if (typeof processStepsField === 'string') {
            if (processStepsField.trim().startsWith('[') || processStepsField.trim().startsWith('{')) {
              processSteps = JSON.parse(processStepsField);
            } else {
              processSteps = processStepsField.split(/\n|•|·|-/).map(p => p.trim()).filter(p => p.length > 0);
            }
          } else if (Array.isArray(processStepsField)) {
            processSteps = processStepsField;
          }
        } catch (e) {
          console.warn('Failed to parse process steps:', e);
          processSteps = [];
        }
      }
      
      // Parse documents required with language support
      let documentsRequired = [];
      const documentsRequiredField = language === 'ar'
        ? (fields.documents_required_arb || fields.documents_required_ar || fields.documents_required || fields.Documents_Required_arb || fields.Documents_Required_ar || fields.Documents_Required)
        : (fields.documents_required_eng || fields.documents_required_en || fields.documents_required || fields.Documents_Required_eng || fields.Documents_Required_en || fields.Documents_Required);
      
      if (documentsRequiredField) {
        try {
          if (typeof documentsRequiredField === 'string') {
            if (documentsRequiredField.trim().startsWith('[') || documentsRequiredField.trim().startsWith('{')) {
              documentsRequired = JSON.parse(documentsRequiredField);
            } else {
              documentsRequired = documentsRequiredField.split(/\n|•|·|,|;/).map(d => d.trim()).filter(d => d.length > 0);
            }
          } else if (Array.isArray(documentsRequiredField)) {
            documentsRequired = documentsRequiredField;
          }
        } catch (e) {
          console.warn('Failed to parse documents required:', e);
          documentsRequired = [];
        }
      }
      
      // Get duration
      const duration = fields.duration || fields.Duration || '';
      
      const formatted = {
        id: fields.id || fields.Id || fields.ID,
        subservice_id: subserviceId,
        content: language === 'ar' ? (contentAr || contentEn) : (contentEn || contentAr),
        content_en: contentEn,
        content_ar: contentAr,
        features: features,
        process_steps: processSteps,
        process_steps_eng: fields.process_steps_eng || fields.Process_Steps_eng || fields.process_steps_en || fields.process_steps || [],
        process_steps_en: fields.process_steps_en || fields.process_steps_eng || fields.process_steps || [],
        process_steps_arb: fields.process_steps_arb || fields.Process_Steps_arb || fields.process_steps_ar || fields.process_steps || [],
        process_steps_ar: fields.process_steps_ar || fields.process_steps_arb || fields.process_steps || [],
        documents_required: documentsRequired,
        documents_required_eng: fields.documents_required_eng || fields.Documents_Required_eng || fields.documents_required_en || fields.documents_required || [],
        documents_required_en: fields.documents_required_en || fields.documents_required_eng || fields.documents_required || [],
        documents_required_arb: fields.documents_required_arb || fields.Documents_Required_arb || fields.documents_required_ar || fields.documents_required || [],
        documents_required_ar: fields.documents_required_ar || fields.documents_required_arb || fields.documents_required || [],
        duration: duration,
        additional_info: fields.additional_info || fields.Additional_Info || {}
      };
      
      console.log(`✅ Fetched service details from NocoBase`, {
        hasContent: !!contentEn || !!contentAr,
        featuresCount: features.length,
        processStepsCount: processSteps.length,
        documentsRequiredCount: documentsRequired.length,
        hasDuration: !!duration
      });
      return formatted;
    } catch (error) {
      console.error('❌ Failed to fetch service details from NocoBase:', error);
      return null;
    }
  }

  // Clear cache
  clearCache() {
    this.dataCache.clear();
    console.log('🗑️ Cache cleared');
  }

  // Test connection
  async testConnection() {
    try {
      console.log('🔌 Testing NocoBase API Connection...');
      const response = await this.request('/collections:list');
      console.log('✅ NocoBase connection successful!');
      return true;
    } catch (error) {
      console.error('❌ NocoBase connection failed:', error);
      return false;
    }
  }
}

// Export singleton instance with error handling
let nocobaseServiceInstance = null;

try {
  nocobaseServiceInstance = new NocoBaseService();
} catch (error) {
  console.error('❌ Failed to initialize NocoBase service:', error);
  // Create a mock service that fails gracefully
  nocobaseServiceInstance = {
    isAvailable: false,
    getHeroBanners: async () => { 
      console.warn('⚠️ NocoBase service not available');
      return []; 
    },
    getServices: async () => { 
      console.warn('⚠️ NocoBase service not available');
      return []; 
    },
    getSubservices: async () => { 
      console.warn('⚠️ NocoBase service not available');
      return []; 
    },
    getServiceDetails: async () => { 
      console.warn('⚠️ NocoBase service not available');
      return null; 
    },
    getTeamMembers: async () => { 
      console.warn('⚠️ NocoBase service not available');
      return []; 
    },
    getPosts: async () => { 
      console.warn('⚠️ NocoBase service not available');
      return []; 
    },
  };
}

export default nocobaseServiceInstance;

